import React, { useMemo } from 'react';
import {
  Spinner,
  Flex,
  Box,
  OptionType,
  TableColumnType,
  SortDirection,
  Icon,
} from '@fullstory/components';
import { ResourceListTable } from './ResourceListTable';
import { ResourceListControls, defaultStringToFilter } from './ResourceListControls';
import { isEqual, orderBy } from 'lodash';
import { EmptyState, ErrorState, NoMatchState } from './NoResultsStates';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { FilterOptionValueType, Value } from '@fullstory/redux';
import { Action, ItemType, LibrarySortState } from '../../types';

export type ResourceListResourceType =
  | 'Alert'
  | 'Dashboard'
  | 'Funnel'
  | 'Heatmap'
  | 'Journey'
  | 'Metric'
  | 'Segment'
  | 'Retention'
  | 'Page'
  | 'Space';

type ResourceListProps<T, I extends ItemType> = {
  /** The current, filtered set of items to show. */
  items: I[];
  /** The icon to show next to each item. */
  itemIcon: React.ComponentPropsWithoutRef<typeof Icon>['name'];
  /** Callback for when an action is taken on an item. */
  onItemAction?: (action: { id: string; action: Action }) => void;
  /** The filters available. If not provided, the standard filters will be available. */
  filters: OptionType<T>[];
  /** The current set of filters. */
  controlValue: Value<T>;
  /** Callback for when the filters change. */
  onChangeControlValue: (value: Value<T>) => void;
  /** Whether the list is disabled. */
  disabled?: boolean;
  /** Placeholder to show in the search box. */
  searchPlaceholder?: string;
  /** Whether the items are currently loading. */
  isLoading?: boolean;
  /** Component to show when an error has occurred, or undefined if there is no error. */
  errorState?: React.ReactChild;
  /** Component to show when there are no items, or undefined if there are items. */
  emptyState?: React.ReactChild;
  /**
   * Component to show when there are no visible items because of the filters,
   * or undefined if there are visible items.
   */
  noMatchState?: React.ReactChild;

  /** If provided, will make the resource list scrollable. */
  maxHeight?: 'auto' | 500;
  disableSorting: (filter: T) => boolean;
  orderItems: (filter: T, items: I[]) => I[];
  getPagedData?: (items: I[]) => I[];
  middleColumns?: TableColumnType<I>[];
  sortFunction?: (rows: I[], key: string, direction: SortDirection) => I[];
  defaultSort?: LibrarySortState;
  shouldSetSortParams?: boolean;
};

export function defaultOrderItems<I extends ItemType>(filter: FilterOptionValueType, items: I[]) {
  return isEqual(filter, defaultStringToFilter('recent'))
    ? orderBy(
        items,
        item => (item.lastViewed ? Timestamp.toDate(item.lastViewed) : new Date(0)),
        'desc',
      )
    : items;
}

export const defaultDisableSorting = (filter: FilterOptionValueType): boolean =>
  filter === 'recent';

function ResourceList<T, I extends ItemType>({
  items,
  itemIcon,
  onItemAction,
  filters,
  controlValue,
  onChangeControlValue,
  disabled,
  searchPlaceholder,
  isLoading,
  errorState,
  emptyState,
  noMatchState,
  maxHeight,
  disableSorting,
  orderItems,
  getPagedData = items => items,
  middleColumns,
  sortFunction,
  defaultSort,
  shouldSetSortParams,
}: ResourceListProps<T, I>) {
  const renderedItems = useMemo(
    () => orderItems(controlValue.filter, items),
    [items, controlValue.filter, orderItems],
  );
  return (
    <>
      <ResourceListControls<T>
        filters={filters}
        value={controlValue}
        onChange={onChangeControlValue}
        placeholder={searchPlaceholder}
      />
      {(() => {
        if (isLoading) {
          // Always show loading state while fetching data
          return (
            <Box height={300} paddingTop={48}>
              <Flex justifyContent="center">
                <Spinner diameter={50} />
              </Flex>
            </Box>
          );
        }
        // If the error, empty, or noMatch state is present, show it
        if (errorState) return errorState;
        if (emptyState) return emptyState;
        if (noMatchState) return noMatchState;
        // Normal case. Render the table.
        return (
          <Box maxHeight={maxHeight} overflow="auto" style={{ position: 'relative' }}>
            <ResourceListTable
              items={renderedItems}
              icon={itemIcon}
              disabled={disabled}
              onAction={onItemAction}
              disableSorting={disableSorting(controlValue.filter)}
              getPagedData={getPagedData}
              middleColumns={middleColumns}
              sortFunction={sortFunction}
              defaultSort={defaultSort}
              shouldSetSortQueryParams={shouldSetSortParams}
            />
          </Box>
        );
      })()}
    </>
  );
}

const ResourceListWithStatics = ResourceList as typeof ResourceList & {
  EmptyState: typeof EmptyState;
  ErrorState: typeof ErrorState;
  NoMatchState: typeof NoMatchState;
};

ResourceListWithStatics.EmptyState = EmptyState;
ResourceListWithStatics.ErrorState = ErrorState;
ResourceListWithStatics.NoMatchState = NoMatchState;

export { ResourceListWithStatics as ResourceList };
